import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getContentByPublicId } from "../api/publicContent.api";
import SEO from "../components/SEO";
import "../index.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FaCalendarAlt } from "react-icons/fa";
import { Helmet } from 'react-helmet';
import DetailsRenderer from './DetailsRenderer';
import SessionLinks from "../components/SessionLinks";

export default function ContentDetails() {
  const { publicId } = useParams();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await getContentByPublicId(publicId);
        if (mounted) setContent(data);
      } catch (err) {
        console.error(err);
        setError("Content not found.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [publicId]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!content) return null;

  const { title, date, descr, image, type, details, sessions } = content;

  return (
    <>
    <Helmet>
      <title>LCWE | {type}</title>
    </Helmet>
    <div className="container-fluid position-relative p-0">
        <div className="container-fluid bg-primary py-2 bg-header" style={{marginBottom:20, backgroundColor:"#15415c"}}>
            <div className="row py-5">
                <div className="col-12 pt-lg-2 mt-lg-2 text-center">
                    <h1 className="display-4 text-white animated zoomIn">{type.toUpperCase()}</h1>
                    <Link to="/" className="h5 text-white">Home</Link>
                    <i className="bi bi-circle text-white px-2"></i>
                    <Link to="" className="h5 text-white">{type.charAt(0).toUpperCase() + type.slice(1)}</Link>
                </div>
            </div>
        </div>
      </div>
    <SEO
      title={`${content.title} | LCWE`}
      description={content.descr?.slice(0, 160)}
      image={
        content.image
          ? `https://node.lcwe-lb.com/uploads/images/${content.image}`
          : null
      }
      url={`https://node.lcwe-lb.com/content/${content.publicId}`}
      structuredData={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: content.title,
        description: content.descr,
        datePublished: content.date,
        image: content.image
          ? `https://node.lcwe-lb.com/uploads/images/${content.image}`
          : undefined,
        author: {
          "@type": "Organization",
          name: "LCWE"
        }
      }}
    />

    <div className="container-fluid py-5 wow fadeInUp" data-wow-delay="0.1s">
      <div className="container py-5">
        <div className="row g-5">
          <div className="col-lg-8">
            <div className="mb-5">
              <div className="row g-5">
                <div className="col-lg-4">
                  {image && (
                    <img
                      src={`https://node.lcwe-lb.com/uploads/images/${image}`}
                      alt={title}
                      className="img-fluid w-100 rounded mb-5"
                    />
                  )}
                </div>
                <div className="col-lg-8">
                  <h2 className="mb-4">{title}</h2>
                </div>
              </div>
              <div>
                <small>
                  <b><FaCalendarAlt /> {type.charAt(0).toUpperCase() + type.slice(1)} Date:</b>
                  {date && (
                    <p className="date">
                      {new Date(date).toLocaleDateString()}
                    </p>
                  )}
                </small>
                <p style={{textAlign:'justify'}}>{descr}</p>
                <DetailsRenderer type={type} details={details} />
                  
                  {type === "training" && Array.isArray(sessions) && sessions.length > 0 && (
                    <div className="mb-5">
                      <h5>Sessions:</h5>
                      {sessions.map((s) => (
                        <div key={s.id} className="mb-4 p-3 border rounded">
                          <h6>{s.title}</h6>
                          {s.date && (
                            <p><strong>Date: </strong>{new Date(s.date).toLocaleDateString()}</p>
                          )}
                          {s.trainer && <p><strong>Trainer: </strong> {s.trainer}</p>}
                          {s.link && <SessionLinks link={s.link} />}
                          {s.descr && <p><strong>Materials:</strong><br />{s.descr} </p> }
                        </div>
                      ))}
                    </div>
                  )}
              </div>
              {type === "training" && (
                <div className="col-lg-4">
                  <div className="wow slideInUp" data-wow-delay="0.1s">
                    <div className="section-title section-title-sm position-relative pb-3 mb-4">
                      <h3 className="mb-0">Details</h3>
                    </div>
                    <div className="bluet text-center" style={{padding:30}}>
                      <p><b>{date}</b></p>
                      <hr/>
                      <p><b>Language: </b>{details?.language}</p>
                      <hr/>
                      <p><b>Training Type: </b>{details?.training_type}</p>
                      <hr/>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-lg-4">
            <div className="mb-5 wow slideInUp" data-wow-delay="0.1s">
              <div className="section-title section-title-sm position-relative pb-3 mb-4">
                <h3 className="mb-0">Categories</h3>
              </div>
              <div className="link-animated d-flex flex-column justify-content-start">
                <Link to="/blogs" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Blogs</Link>
                <Link to="/training" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Training Courses</Link>
                <Link to="/events" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Events & Webinars</Link>
                <Link to="/projects" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Projects</Link>
                <Link to="/publications" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Publications</Link>
                <Link to="/reports" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Reports</Link>
                <Link to="/livinglab" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Living Lab</Link>
                <Link to="/workshops" style={{ textDecoration: "none" }} className="h5 fw-semi-bold bg-blue rounded py-2 px-3 mb-2 text-decoration-none text-reset"><i className="bi bi-arrow-right me-2"></i>Workshops</Link>
              </div>
            </div>       
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
